import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Plus1 } from 'blaze-ui/icons/24'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Minus } from 'almanac-ui/icons'
import { ContentType } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { ExternalIntegrationEnum, ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { BrandPlanSchedule } from 'types'
import cx from 'classnames'
import { AutopilotProPill } from 'components/AutopilotUpgradeButtons'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { pluralize } from 'utils'
import { channelTypeToField, getCountFromDirectProperties } from './utils'
import { Additionals } from './Types'
import Styles from './AddMoreContentModal.module.scss'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import ToggleButton from 'common/ToggleButton/ToggleButton'

const MAX_ADDITIONAL_PER_CHANNEL = 10
const MAX_TOTAL_PER_CHANNEL = 100

// Mapping from ContentType to ExternalIntegrationEnum
const contentTypeToProvider: Record<ContentType, ExternalIntegrationEnum | null> = {
  instagram_post: ExternalIntegrationEnum.Instagram,
  instagram_story: ExternalIntegrationEnum.Instagram,
  instagram_reel: ExternalIntegrationEnum.Instagram,
  tiktok_post: ExternalIntegrationEnum.TikTok,
  facebook_post: ExternalIntegrationEnum.Facebook,
  linkedin_post: ExternalIntegrationEnum.LinkedIn,
  twitter_post: ExternalIntegrationEnum.X,
  youtube_short: ExternalIntegrationEnum.YouTube,
  newsletter: ExternalIntegrationEnum.Mailchimp,
  blog_post: null, // Will be determined by connected blog integration
  crosspost_post: null, // Has multiple providers
  video_crosspost_post: null, // Has multiple providers
  google_my_business_post: ExternalIntegrationEnum.GoogleBusinessProfile,
} as any

export interface Channel {
  name: string
  type: ContentType
  icon: any
  isCrosspost?: boolean
  providers?: ExternalIntegrationEnum[]
}

interface ChannelRowProps {
  channel: Channel
  index: number
  channelCounts?: Record<ContentType, number>
  additionals: Additionals
  disabledChannels: string[]
  maxChannels: number
  usedChannels: ContentType[]
  showLimits: boolean
  maxPostsPerChannel: number
  targetSchedule?: BrandPlanSchedule
  exceedsMax: boolean
  showScheduledCount?: boolean
  externalIntegrations?: ExternalIntegration[]
  excludedAccounts?: Record<number, boolean>
  onToggleAccount?: (accountId: number, checked: boolean) => void
  onAdd: () => void
  onRemove: () => void
}

export const ChannelRow = ({
  channel,
  index,
  channelCounts,
  additionals,
  disabledChannels,
  maxChannels,
  usedChannels,
  showLimits,
  maxPostsPerChannel,
  targetSchedule,
  exceedsMax,
  showScheduledCount = false,
  externalIntegrations = [],
  excludedAccounts = {},
  onToggleAccount,
  onAdd,
  onRemove,
}: ChannelRowProps) => {
  const scheduledCount = getCountFromDirectProperties(channel.type, targetSchedule)
  const now = channelCounts?.[channel.type] !== undefined ? channelCounts[channel.type] : scheduledCount
  const additional = additionals[channel.type] || 0
  const total = now + additional
  const isProOnly = disabledChannels.includes(channelTypeToField[channel.type])
  const disabledFromChannelLimit = maxChannels <= usedChannels.length && !usedChannels.includes(channel.type)
  const disabled = isProOnly || disabledFromChannelLimit
  const showLockButtonForChannel = showLimits && total >= maxPostsPerChannel

  // Get accounts for this channel
  const provider = contentTypeToProvider[channel.type]
  const channelAccounts = externalIntegrations.filter((integration) => {
    if (channel.isCrosspost && channel.providers) {
      return channel.providers.includes(integration.provider)
    }
    return provider && integration.provider === provider
  })

  // Don't show account toggles for crosspost types
  const isCrosspostType = channel.type === 'crosspost_post' || channel.type === 'video_crosspost_post'
  const showAccounts = !isCrosspostType && channelAccounts.length > 1

  let lockedTooltipMessage = ''
  if (isProOnly) {
    lockedTooltipMessage = 'Upgrade to enable cross posts'
  } else if (showLockButtonForChannel && total < MAX_TOTAL_PER_CHANNEL) {
    lockedTooltipMessage = `Upgrade to enable more than ${maxPostsPerChannel} ${pluralize(
      'post',
      maxPostsPerChannel,
    )}/week`
  } else if (disabledFromChannelLimit) {
    lockedTooltipMessage = `To enable this channel, remove another first`
  } else if (total >= MAX_TOTAL_PER_CHANNEL) {
    lockedTooltipMessage = `Maximum of ${MAX_TOTAL_PER_CHANNEL} ${pluralize(
      'post',
      MAX_TOTAL_PER_CHANNEL,
    )} per channel per week`
  }

  return (
    <>
      <div key={`${channel.type}-${index}`} className={Styles.row}>
        <div className={Styles.channelNameContainer}>
          <div className={cx(Styles.channelName, { [Styles.disabledContent]: disabled })}>
            {channel.isCrosspost ? <channel.icon providers={channel.providers} /> : <channel.icon size={20} />}
            <Heading level={5}>{channel.name}</Heading>
          </div>
          {isProOnly && (
            <Tooltip placement="top" label="Upgrade to enable cross posts">
              <div>
                <AutopilotProPill />
              </div>
            </Tooltip>
          )}
        </div>
        <div className={Styles.count}>
          {showScheduledCount && (
            <div className={Styles.currentCount}>
              <Text variant="placeholder">{now}</Text>
            </div>
          )}
          <div className={Styles.additional}>
            <IconButton icon={Minus} size="sm" variant="ghost-tertiary" isDisabled={disabled} onPress={onRemove} />
            <div
              className={cx(Styles.additionalNumber, {
                [Styles.hasAdditional]: additional > 0,
                [Styles.disabledContent]: disabled,
              })}
            >
              {additional}
            </div>
            <Tooltip label={lockedTooltipMessage} placement="top" hide={!lockedTooltipMessage}>
              <div>
                <IconButton
                  icon={Plus1}
                  size="sm"
                  variant="ghost-tertiary"
                  isDisabled={
                    disabled ||
                    additional >= MAX_ADDITIONAL_PER_CHANNEL ||
                    total >= MAX_TOTAL_PER_CHANNEL ||
                    exceedsMax ||
                    showLockButtonForChannel
                  }
                  onPress={onAdd}
                />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>

      {showAccounts &&
        channelAccounts.map((account) => {
          const { name, avatar } = extractProfileInfo(account)
          return (
            <div key={account.id} className={Styles.accountRow}>
              <div className={Styles.accountNameContainer}>
                <Avatar size={20} label={getInitials(name)} url={avatar || Silhouette} />
                <Text variant="smallList" color="var(--dark-80)">
                  {name}
                </Text>
              </div>
              <div className={Styles.count}>
                <div className={Styles.currentCount}>
                  <ToggleButton
                    checked={!excludedAccounts[account.id]}
                    name={`account-${account.id}`}
                    handleChange={(e) => onToggleAccount?.(account.id, e.target.checked)}
                    size="sm-md"
                  />
                </div>
                <div className={Styles.additional} />
              </div>
            </div>
          )
        })}
    </>
  )
}
